import { useState, useEffect } from "react";
import TableContainer from "../../Containers/RecordingsManagement/TableContainer/TableContainer";
import { getRecordedCallsList } from "../../Services/AnalyticsService";
import "./RecordingComponent.css";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Spinner, Button, Tooltip } from "@fluentui/react-components";
import { FilterDismiss20Regular } from "@fluentui/react-icons";
export default function RecordingComponent(props) {
  const { t } = useTranslation();
  const [filteredRecordings, setFilteredRecordings] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  
  // Get all callflows from Redux.
  // ⚠️ PAS de "|| {}" : au refresh, CALL_FLOWS est null → "|| {}" recréerait un objet
  // à chaque render → réf instable → l'effet recordingScope (dep allCallFlows) boucle
  // sur setFilters → "Maximum update depth". On garde null (réf stable) ; getCallFlowsArray
  // et toArray gèrent déjà null.
  const allCallFlows = useSelector(state => state.reducer.CALL_FLOWS);
  // Agents IA : leurs enregistrements sont rangés sous FLOW_ID = id agent IA.
  // Même raison : pas de "|| {}".
  const agentAIs = useSelector(state => state.reducer.AGENT_AIS);
  
  // Get tenantId from props or global context
  const tenantId = props.tenantId;
  // Default columns configuration
  const getDefaultColumns = () => [
    { key: "audio", labelKey: "RECORDING", visible: true },
    { key: "direction", labelKey: "DIRECTION", visible: true },
    { key: "recordingScope", labelKey: "RECORDING_SCOPE", visible: true },
    { key: "callerId", labelKey: "CALLER_ID", visible: true },
    { key: "duration", labelKey: "DURATION", visible: true },
    { key: "agentName", labelKey: "AGENT_NAME", visible: true },
    { key: "date", labelKey: "DATE", visible: true },
    { key: "transcription", labelKey: "TRANSCRIPTION", visible: true },
    { key: "callId", labelKey: "CALL_ID", visible: true },
  ];

  // Load columns from localStorage or use default
  const loadColumns = () => {
    const savedColumns = localStorage.getItem("recordingTableColumns");
    return savedColumns ? JSON.parse(savedColumns) : getDefaultColumns();
  };

  const [columns, setColumns] = useState(loadColumns);

  // Save columns to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem("recordingTableColumns", JSON.stringify(columns));
  }, [columns]);

  const handleColumnsChange = (newColumns) => {
    setColumns(newColumns);
  };

  // Get date range from today to one month back
  const getCurrentMonthDateRange = () => {
    const now = new Date();
    
    // Today's date at 23:59:59.999 in local time (end)
    const endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
    
    // One month back at midnight in local time (start)
    const startDate = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate(), 0, 0, 0, 0);
    
    return { startDate: startDate.getTime(), endDate: endDate.getTime() };
  };

  // Initialize filters with current month date range
  const [filters, setFilters] = useState({
    direction: "",
    recordingScope: [],
    callerId: "",
    agentName: "",
    agentIds: [],
    callId: "",
    duration: { Min: "", Max: "" },
    date: { startDate: getCurrentMonthDateRange().startDate, endDate: getCurrentMonthDateRange().endDate },
  });

  // Function to update filters
  const onFilterChange = (updatedFilters) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...updatedFilters,
    }));
  };
  
  // Helper function to convert callflows data to array
  const getCallFlowsArray = () => {
    if (!allCallFlows) return [];
    if (Array.isArray(allCallFlows)) return allCallFlows;
    return Object.values(allCallFlows);
  };
  
  // Calcule l'ensemble des scopes par défaut : tous les callflows + agents IA +
  // agents supervisés (utilisé à l'init et au "Clear filters").
  const getAllScopeIds = () => {
    const callFlowsArray = getCallFlowsArray();
    const allCallFlowIds = callFlowsArray.map(flow => flow.id).filter(id => id);
    // IDs des agents IA (leurs recordings sont sous FLOW_ID = id agent IA)
    const iaAgentList = !agentAIs ? [] : (Array.isArray(agentAIs) ? agentAIs : Object.values(agentAIs));
    const iaAgentIds = iaAgentList.map(a => a?.id).filter(Boolean);
    return [
      ...new Set([
        ...allCallFlowIds,
        ...iaAgentIds,
        ...(Array.isArray(props.supervisedAgentIds) ? props.supervisedAgentIds : [])
      ])
    ];
  };

  // Update recordingScope - initialize with ALL callflows + agents by default
  useEffect(() => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      recordingScope: getAllScopeIds(),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allCallFlows, agentAIs, props.supervisedAgentIds]);

  // Réinitialise TOUS les filtres à leur valeur par défaut (scopes = tous,
  // dates = mois courant, reste vide).
  const clearFilters = () => {
    setFilters({
      direction: "",
      recordingScope: getAllScopeIds(),
      callerId: "",
      agentName: "",
      agentIds: [],
      callId: "",
      duration: { Min: "", Max: "" },
      date: { startDate: getCurrentMonthDateRange().startDate, endDate: getCurrentMonthDateRange().endDate },
    });
  };

  // Call the service with filters to retrieve data.
  // Source = Data_Pipe (doc CDR Cosmos, RECORDING_ENABLED=true) au lieu de la table
  // legacy. On mappe la réponse vers la même forme que celle attendue par le tableau,
  // pour ne RIEN changer à l'UI (colonnes, filtres, style).
  useEffect(() => {
    if (!(filters.recordingScope.length > 0 && tenantId)) return;

    // Débounce : on ne déclenche le backend qu'une fois la saisie stabilisée.
    // Évite un fetch à chaque frappe (filtre Duration notamment) → plus de
    // résultats intermédiaires qui "disparaissent" ni de clignotement.
    const handle = setTimeout(() => {
      // ⚠️ on NE vide PAS la liste avant le fetch : on garde les lignes affichées
      // pendant le chargement (le spinner suffit) → pas de flash de table vide.
      setIsLoading(true);
      const backendFilters = {
        recordingScopes: filters.recordingScope,
        direction: filters.direction || undefined,
        callerId: filters.callerId || undefined,
        agentName: filters.agentName || undefined,
        agentIds: (filters.agentIds && filters.agentIds.length) ? filters.agentIds : undefined,
        callId: filters.callId || undefined,
        durationMin: filters.duration?.Min || undefined,
        durationMax: filters.duration?.Max || undefined,
        startDate: filters.date?.startDate ? new Date(filters.date.startDate).toISOString() : undefined,
        endDate: filters.date?.endDate ? new Date(filters.date.endDate).toISOString() : undefined,
      };
      // pageSize large : le tableau gère sa propre pagination côté client (comme avant)
      getRecordedCallsList(tenantId, backendFilters, null, 500)
        .then((res) => {
          const mapped = (res.data || []).map((r) => ({
            RowKey: r.callId,
            PartitionKey: r.recordingScopeLabel,
            DIRECTION: r.direction,
            CALLER_ID: r.callerNumber,
            CALLER_TYPE: r.callerType,
            FLOW_NAME: r.recordingScopeLabel,
            AGENT_DISPLAYNAME: r.agentName,
            CALL_HANDLE_TIME: r.duration,
            // secondes brutes : la cellule date fait formatTimestamp(value * 1000)
            T_CALL_ANSWERED: r.answeredAt || 0,
            isTranscriptionActive: r.transcriptionAvailable,
            transcriptionText: "",
          }));
          setFilteredRecordings(mapped);
        })
        .catch((error) => {
          console.error("Error retrieving recordings:", error);
          setFilteredRecordings([]);
        })
        .finally(() => setIsLoading(false));
    }, 450);

    // Si filters change à nouveau avant la fin du délai, on annule le fetch précédent.
    return () => clearTimeout(handle);
  }, [filters, tenantId]);

  return (
    <div className="recording-container">
      <h2>{t("RECORDINGS")}</h2>
      {/* Bouton flottant "Clear filters", aligné au-dessus du bouton réglages (⚙️). */}
      <Tooltip withArrow relationship="label" content={t("CLEAR_FILTERS", { defaultValue: "Clear filters" })}>
        <Button
          appearance="primary"
          icon={<FilterDismiss20Regular />}
          onClick={clearFilters}
          className="floating-clear-button"
          aria-label={t("CLEAR_FILTERS", { defaultValue: "Clear filters" })}
        />
      </Tooltip>
      {isLoading && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0" }}>
          <Spinner size="tiny" />
          <span>{t("LOADING", { defaultValue: "Loading…" })}</span>
        </div>
      )}
      <TableContainer
        data={filteredRecordings}
        onFilterChange={onFilterChange}
        filters={filters}
        columns={columns}
        onColumnsChange={handleColumnsChange}
        allCallFlows={allCallFlows}
      />
    </div>
  );
}
